import { createContext, useContext, type ReactNode } from 'react';
import type { ConsoleRole } from './api';
import { labels } from './labels';

/**
 * What a signed-in user can do (ADR 0041). The roles nest: `admin` holds everything `viewer`
 * does. A viewer sees every screen and writes nothing, so each write names the lowest role it
 * needs. Every write needs `admin`, so one action stands for "can write".
 */
export type Action = 'administer';

export interface Session {
  username: string;
  role: ConsoleRole;
  /** SSO-22: a member of the group Super admins. It changes no right, only the menu. */
  superAdmin: boolean;
  userAdminUrl?: string;
  can: (action: Action) => boolean;
}

export function sessionFor(
  username: string,
  role: ConsoleRole,
  { superAdmin = false, userAdminUrl }: { superAdmin?: boolean; userAdminUrl?: string } = {},
): Session {
  return { username, role, superAdmin, userAdminUrl, can: () => role === 'admin' };
}

// `App` always provides the session. A screen rendered with no provider (a spec of one screen)
// gets full access: the backend refuses a write the role cannot do, so this default only skips
// the disabled state.
const SessionContext = createContext<Session>(sessionFor('', 'admin'));

export function SessionProvider({ session, children }: { session: Session; children: ReactNode }) {
  return <SessionContext.Provider value={session}>{children}</SessionContext.Provider>;
}

export function useSession(): Session {
  return useContext(SessionContext);
}

/**
 * The props that turn a write control off for a role below the one it needs. The `title` is the
 * same text `RoleNote` shows, so a disabled control in a long list says why on hover and focus.
 */
export function useGate(action: Action): { allowed: boolean; props: { disabled?: true; title?: string } } {
  const allowed = useSession().can(action);
  return { allowed, props: allowed ? {} : { disabled: true, title: labels.roleDenied } };
}

/** The visible note beside a disabled write control (`action-row__note`). */
export function RoleNote({ action }: { action: Action }) {
  return useSession().can(action) ? null : <p className="action-row__note">{labels.roleDenied}</p>;
}
